import { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ReactComponent as PlusIcon } from '@rocco/icons/svg/plus-no-padding.svg';
import { Button } from '@rocco/components/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Input } from '@rocco/components/input';
import { cn } from '@rocco/utils/cn';
import {
    DesignBoardTile,
    DesignBoardTileSkeleton,
} from '@rocco/ui/design-board/components/DesignBoardTile';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
} from '@rocco/components/drawer';
import { useBuildHubBoardModal } from '~/modules/build-hub/hooks/use-build-hub-board-modal';
import { useBuildHubBoardInspirationActions } from '@modules/build-hub/hooks/use-build-hub-board-inspiration-actions';
import { useDesignBoards } from '~/ui-rocco/hooks/use-design-boards';
import type { GetDesignBoardListItemResponse } from '@modules-rocco/design-board/api/get-design-board-list.server';
import { DocumentAccessControl } from '@modules/build-hub/components/document-access';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import {
    DOCUMENT_ACCESS_MODE,
    DOCUMENT_ACCESS_MODE_VALUES,
    DOCUMENT_ROLE,
    DOCUMENT_ROLE_VALUES,
    type DocumentAccessMode,
    type DocumentRole,
    type DocumentCollaborator,
    type DocumentCollaboratorSelection,
} from '@modules/build-hub/types';

/**
 * Modal for selecting and linking design boards to a BuildHub project.
 * Supports both selecting existing boards and creating new boards.
 */
export const SelectBuildHubBoardModal = () => {
    const {
        isOpened,
        isCreating,
        buildHubId,
        buildTitle,
        buildHubCollaborators,
        closeModal,
        isBoardLinked,
        isBoardLinking,
    } = useBuildHubBoardModal();
    const {
        linkBoardToBuildHub,
        createAndLinkBoard,
        isCreating: isCreatingBoard,
    } = useBuildHubBoardInspirationActions();
    const { boards, isLoading, handleFetchBoards } = useDesignBoards();
    const { user } = useUserLoggedIn();
    const [isAddingNewBoard, setIsAddingNewBoard] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTitleEmpty, setIsTitleEmpty] = useState(true);
    const formRef = useRef<{ submit: () => void }>(null);

    // Fetch boards when modal opens
    useEffect(() => {
        if (isOpened && !boards && !isLoading) {
            handleFetchBoards();
        }
    }, [isOpened, boards, handleFetchBoards, isLoading]);

    // Reset form state when modal opens
    useEffect(() => {
        if (isOpened) {
            setIsAddingNewBoard(!!isCreating);
            setIsSubmitting(false);
            setIsTitleEmpty(true);
        }
    }, [isOpened, isCreating]);

    const hasBoards = boards && boards.length > 0;
    const isFetchingBoards = isLoading && !boards;

    const handleBoardSelect = (boardId: number) => {
        const board = boards?.find((b) => b.id === boardId);
        if (board && !isBoardLinked(board.id)) {
            linkBoardToBuildHub(board.id, buildHubId, closeModal);
        }
    };

    const handleBoardCreate = (
        title: string,
        description: string,
        accessMode: DocumentAccessMode,
        accessRole: DocumentRole,
        futureTeamMemberRole: DocumentRole | 'no-access'
    ) => {
        setIsSubmitting(true);
        createAndLinkBoard(
            {
                title,
                description,
                accessMode,
                accessRole,
                futureTeamMemberRole,
            },
            buildHubId,
            () => {
                setIsSubmitting(false);
                closeModal();
            }
        );
    };

    const handleModalClose = () => {
        setIsAddingNewBoard(false); // Reset form state
        closeModal();
    };

    return (
        <Drawer
            open={isOpened}
            handleOnly={true}
            onOpenChange={handleModalClose}
        >
            <DrawerContent
                variant="glassy"
                overlayVariant="dark"
                noHandle={true}
            >
                <div className="flex h-full w-full flex-col">
                    <DrawerHeader variant="border" alignment="left">
                        <DrawerTitle>
                            {isAddingNewBoard
                                ? 'New Mood Board'
                                : 'Import Boards to this Project'}
                        </DrawerTitle>
                        {!isAddingNewBoard && (
                            <DrawerDescription>
                                <span>
                                    Import boards to <b>{buildTitle}</b> and
                                    give your project team instant access to
                                    your design inspiration.
                                </span>
                            </DrawerDescription>
                        )}
                    </DrawerHeader>

                    <div
                        className={cn(
                            'scrollbar-hidden w-dvw shrink overflow-y-scroll transition-all duration-300 ease-in-out md:w-[56.125rem] md:px-6',
                            isAddingNewBoard
                                ? 'max-h-[80vh] min-h-fit'
                                : 'h-[34.25rem] md:h-[26.5rem]'
                        )}
                    >
                        {isAddingNewBoard ? (
                            <AddNewBoard
                                onCreateNewBoard={handleBoardCreate}
                                formRef={formRef}
                                buildHubId={buildHubId}
                                buildTitle={buildTitle}
                                buildHubCollaborators={buildHubCollaborators}
                                ownerEmail={
                                    user.__typename === 'Me'
                                        ? user.Email
                                        : undefined
                                }
                                onTitleChange={setIsTitleEmpty}
                            />
                        ) : isFetchingBoards ? (
                            <BoardSelectionLoading />
                        ) : hasBoards ? (
                            <BoardSelectionGrid
                                boards={boards}
                                onBoardSelect={handleBoardSelect}
                                isBoardLinked={isBoardLinked}
                                isBoardLinking={isBoardLinking}
                            />
                        ) : (
                            <EmptyBoardsState
                                onCreateBoard={() => setIsAddingNewBoard(true)}
                            />
                        )}
                    </div>

                    {isAddingNewBoard && (
                        <DrawerFooter variant="glassy">
                            <Button
                                size="xLarge"
                                color="secondary"
                                minWidth="200"
                                onClick={closeModal}
                                disabled={isSubmitting}
                            >
                                <span className="text-lg">Cancel</span>
                            </Button>
                            <Button
                                size="xLarge"
                                minWidth="200"
                                onClick={() => formRef.current?.submit()}
                                disabled={isTitleEmpty || isSubmitting}
                                isLoading={isSubmitting || isCreatingBoard}
                            >
                                <span className="text-lg">
                                    {isSubmitting
                                        ? 'Creating...'
                                        : 'Create Board'}
                                </span>
                            </Button>
                        </DrawerFooter>
                    )}
                </div>
            </DrawerContent>
        </Drawer>
    );
};

/**
 * Loading state for board selection using shared skeleton components
 */
const BoardSelectionLoading = () => {
    return (
        <div className="grid select-none grid-cols-2 gap-x-2 gap-y-6 px-2 py-[1.1875rem] md:grid-cols-3 md:gap-y-12 md:px-0 md:py-8">
            {/* Create new board skeleton */}
            <CreateNewBoardButton onCreateNewBoard={() => {}} />

            {/* Board skeletons */}
            {[0, 1, 2, 3, 4, 5].map((index) => (
                <DesignBoardTileSkeleton key={index} />
            ))}
        </div>
    );
};

/**
 * Board selection grid with create new board option using shared components
 */
const BoardSelectionGrid = ({
    boards,
    onBoardSelect,
    isBoardLinked,
    isBoardLinking,
}: {
    boards: GetDesignBoardListItemResponse[];
    onBoardSelect: (boardId: number) => void;
    isBoardLinked: (boardId: number) => boolean;
    isBoardLinking: (boardId: number) => boolean;
}) => {
    return (
        <div className="grid select-none grid-cols-2 gap-x-2 gap-y-6 px-2 py-[1.1875rem] md:grid-cols-3 md:gap-y-12 md:px-0 md:py-8">
            {/* Board selection items using shared DesignBoardTile */}
            {boards.map((board) => (
                <DesignBoardTile
                    key={board.id}
                    board={board}
                    isSelected={isBoardLinked(board.id)}
                    isLoading={isBoardLinking(board.id)}
                    showHoverButton={true}
                    buttonText="Link Board"
                    onTileClick={onBoardSelect}
                />
            ))}
        </div>
    );
};

/**
 * Create new board button matching the original design board selector
 */
const CreateNewBoardButton = ({
    onCreateNewBoard,
}: {
    onCreateNewBoard: () => void;
}) => {
    return (
        <button
            onClick={onCreateNewBoard}
            className="flex aspect-[4/2.9] w-full flex-col items-center justify-start rounded-lg border border-dashed border-gray-900 transition-colors hover:border-gray-900/90 md:aspect-[4/3]"
        >
            <div className="mt-10 flex size-6 items-center justify-center text-gray-500 md:mt-14">
                <PlusIcon />
            </div>
            <span className="mt-8.5 text-sm text-gray-900">New Board</span>
        </button>
    );
};

/**
 * Empty state when no boards exist
 */
const EmptyBoardsState = ({ onCreateBoard }: { onCreateBoard: () => void }) => {
    return (
        <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <PlusIcon />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
                No mood boards yet
            </h3>
            <p className="mb-6 text-gray-600">
                Create your first mood board to get started
            </p>
            <Button onClick={onCreateBoard}>Create New Board</Button>
        </div>
    );
};

/**
 * Form for creating a new design board with Google Docs-style access control
 */
const AddNewBoard = ({
    onCreateNewBoard,
    formRef,
    buildHubId: _buildHubId,
    buildTitle: _buildTitle,
    buildHubCollaborators,
    ownerEmail,
    onTitleChange,
}: {
    onCreateNewBoard: (
        title: string,
        description: string,
        accessMode: DocumentAccessMode,
        accessRole: DocumentRole,
        futureTeamMemberRole: DocumentRole | 'no-access'
    ) => void;
    formRef?: React.RefObject<{ submit: () => void }>;
    buildHubId: number;
    buildTitle: string;
    buildHubCollaborators: DocumentCollaborator[];
    ownerEmail?: string;
    onTitleChange?: (isEmpty: boolean) => void;
}) => {
    const [selectedCollaborators, setSelectedCollaborators] = useState<
        DocumentCollaboratorSelection[]
    >([]);
    const [futureTeamMemberRole, setFutureTeamMemberRole] = useState<
        DocumentRole | 'no-access'
    >(DOCUMENT_ROLE.Commenter);
    const [linkAccessRole, setLinkAccessRole] = useState<
        DocumentRole | 'no-access'
    >('no-access');

    const formSchema = z.object({
        title: z.string().min(1, 'Title is required'),
        description: z.string().optional(),
        accessMode: z.enum(DOCUMENT_ACCESS_MODE_VALUES),
        accessRole: z.enum(DOCUMENT_ROLE_VALUES),
    });

    type FormValues = z.infer<typeof formSchema>;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: '',
            description: '',
            accessMode: DOCUMENT_ACCESS_MODE.Restricted,
            accessRole: DOCUMENT_ROLE.Editor,
        },
    });

    // Watch title field and notify parent of empty state
    const titleValue = form.watch('title');
    useEffect(() => {
        if (onTitleChange) {
            onTitleChange(!titleValue || titleValue.trim().length === 0);
        }
    }, [titleValue, onTitleChange]);

    const onSubmit = (values: FormValues) => {
        // Determine access mode and role based on link access setting
        const finalAccessMode =
            linkAccessRole === 'no-access'
                ? DOCUMENT_ACCESS_MODE.Restricted
                : DOCUMENT_ACCESS_MODE.Link;
        const finalAccessRole =
            linkAccessRole === 'no-access'
                ? DOCUMENT_ROLE.Viewer
                : linkAccessRole;

        onCreateNewBoard(
            values.title,
            values.description ?? '',
            finalAccessMode,
            finalAccessRole,
            futureTeamMemberRole
        );
    };

    useImperativeHandle(formRef, () => ({
        submit: () => form.handleSubmit(onSubmit)(),
    }));

    return (
        <div className="w-full px-6 py-8 md:px-0">
            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-8 transition-all duration-300 ease-in-out"
                    id={'build-hub-design-board-modal-form'}
                >
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>
                                    <span className="text-base font-medium leading-tight">
                                        * Title
                                    </span>
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        variant="small"
                                        color="contrast"
                                        text="default"
                                        type="text"
                                        placeholder="My First Mood Board"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>
                                    <span className="text-base font-medium leading-tight">
                                        Description
                                    </span>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        color="contrast"
                                        placeholder="Enter Description"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    /> */}

                    {/* Access Control Section */}
                    <div className="space-y-2 transition-all duration-300 ease-in-out">
                        <DocumentAccessControl
                            buildHubCollaborators={buildHubCollaborators}
                            selectedCollaborators={selectedCollaborators}
                            onCollaboratorsChange={(newCollaborators) => {
                                setSelectedCollaborators(
                                    newCollaborators.map((c) => ({
                                        email: c.email,
                                        role: c.role,
                                        name: c.name,
                                    }))
                                );
                            }}
                            disabled={false}
                            ownerEmail={ownerEmail}
                            futureTeamMemberRole={futureTeamMemberRole}
                            onFutureTeamMemberRoleChange={
                                setFutureTeamMemberRole
                            }
                            linkAccessRole={linkAccessRole}
                            onLinkAccessRoleChange={setLinkAccessRole}
                        />
                    </div>
                </form>
            </Form>
        </div>
    );
};
